import React, { useState } from "react";
import { StatCard } from "../../components/ui/card/Cards";
import MealIcon from "../../svg/MealIcon";
import { PageHeading } from "../../components/ui/PageHeading";
import DonutChart from "../../components/ui/graph/DonutChart";
import GaugeChart from "../../components/ui/graph/GaugeChart";
import CommonDataTable from "../../components/ui/table/CommonTable";
import { Button } from "../../components/ui/button";
import {
  useGetAnalyticsSummaryQuery,
  useGetConsumerInformationQuery,
  useLazyExportAnalyticsExcelQuery,
} from "../../featureSlice/analtyicsSliceApi";
import MealAccordion from "./MealAccordian";
import AnaltyicsFilter from "./AnaltyicsFilter";

const Analytics = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    date: "",
    shift: "",
    consumer_type: "",
  });

  const { data: analyticsData } = useGetAnalyticsSummaryQuery();
  const { data: consumerData } = useGetConsumerInformationQuery({
    page,
    limit: rowsPerPage,
    ...filters,
  });

  const currentMeal = analyticsData?.data?.[0]?.cards_data?.current || {};
  const nextMeal = analyticsData?.data?.[0]?.cards_data?.next || {};
  const menu = analyticsData?.data?.[0]?.cards_data?.menu || [];

  const rows = consumerData?.data || [];

  const [exportExcel, { isFetching: isExporting }] =
    useLazyExportAnalyticsExcelQuery();

  const chartData = [
    { label: "Employee", value: currentMeal?.employee || 0 },
    { label: "Visitor", value: currentMeal?.visitor || 0 },
  ];

  const columns = [
    { name: "ID", selector: (row) => row?.consumer_id },
    { name: "Name", selector: (row) => row?.consumer_name },
    {
      name: "User Type",
      cell: (row) => {
        const isEmployee = row?.consumer_type === "Employee";
        return (
          <span
            className={`text-xs px-2 py-1 rounded font-medium ${
              isEmployee
                ? "bg-orange-100 text-orange-700"
                : "bg-purple-100 text-purple-700"
            }`}
          >
            {isEmployee
              ? `${row.consumer_type} (${row.consumer_type_subCategory})`
              : `${row.consumer_type} (${row.card_token_number})`}
          </span>
        );
      },
    },
    {
      name: "Meal Type",
      cell: (row) => (
        <span className="text-xs px-2 py-1 rounded border border-yellow-400 text-yellow-700">
          {row?.meal_type}
        </span>
      ),
    },
    {
      name: "Time In",
      selector: (row) => {
        if (!row?.in_time) return "";
        const utcDate = new Date(row?.in_time);
        const istDate = new Date(utcDate.getTime() + 5.5 * 60 * 60 * 1000);
        return istDate.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        });
      },
    },
    {
      name: "Meal Served",
      cell: (row) => (
        <span
          className={`text-xs px-2 py-1.5 rounded font-medium ${
            row?.meal_served
              ? "bg-green-100 text-green-600 border border-green-500"
              : "bg-red-100 text-red-600 border border-red-500"
          }`}
        >
          {row?.meal_served ? "Consumed" : "Pending"}
        </span>
      ),
    },
  ];

  const handleExport = async () => {
    try {
      const blob = await exportExcel({
        date: filters.date,
        shift: filters.shift,
        consumer_type: filters.consumer_type,
      }).unwrap();

      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "analytics-export.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export failed", err);
    }
  };

  return (
    <>
      <PageHeading title="Analytics" className="mx-3 md:mx-5" />

      <div className="grid grid-cols-1 md:grid-cols-12 gap-5 md:gap-10 p-3 md:p-4">
        <div className="md:col-span-8 space-y-5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 md:gap-6">
            <div className="col-span-12 lg:col-span-7 space-y-4 md:space-y-5">
              <div className="grid grid-cols-2 gap-3 md:gap-6">
                <StatCard
                  title={`Current Meal (${currentMeal?.meal_type || ""})`}
                  value={currentMeal?.employee || 0}
                  subValue={`Visitor: ${currentMeal?.visitor || 0}`}
                  variant="primary"
                  className="w-full py-4 bg-primary text-white card-bg-image"
                  icon={
                    <MealIcon className="bg-white text-primary p-1 rounded-md" />
                  }
                />
                <StatCard
                  title="User Type"
                  className="w-full"
                  chart={
                    <div className="flex items-center justify-between">
                      <div className="ml-2 md:ml-4 text-xs md:text-sm space-y-1.5 md:space-y-2">
                        {chartData.map((item, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-1.5 md:gap-2"
                          >
                            <div
                              className="w-2.5 h-2.5 md:w-3 md:h-3 rounded-full flex-shrink-0"
                              style={{
                                backgroundColor:
                                  index === 0 ? "#FFA500" : "#B266FF",
                              }}
                            />
                            <span className="truncate">{item.label}</span>
                          </div>
                        ))}
                      </div>
                      <DonutChart
                        width={70}
                        height={70}
                        data={chartData}
                        valueKey="value"
                        labelKey="label"
                        colors={["#FFA500", "#B266FF"]}
                        showTooltip
                      />
                    </div>
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-3 md:gap-6">
                <StatCard
                  title="Pending"
                  value={currentMeal?.pending}
                  className="w-full"
                  icon={
                    <MealIcon className="bg-lightRed text-darkRed p-1 rounded-md" />
                  }
                />
                <StatCard
                  title="Consumed"
                  value={currentMeal?.consumed || 0}
                  className="w-full"
                  icon={
                    <MealIcon className="bg-lightGreen text-darkGreen p-1 rounded-md" />
                  }
                />
              </div>
            </div>

            <div className="col-span-12 lg:col-span-5">
              <StatCard
                title={`Upcoming Meal (${nextMeal?.meal_type || ""})`}
                className="w-full h-full"
                chart={
                  <div>
                    <GaugeChart
                      data={chartData}
                      colors={["#FFA500", "#B266FF"]}
                      showCenterValue
                      className="ml-[-1px]"
                    />
                    <div className="flex gap-4 md:gap-6 text-xs md:text-sm mt-2">
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#FFA500]" />
                        <span>Employee</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-[#B266FF]" />
                        <span>Visitor</span>
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>

        <div className="flex flex-wrap justify-between items-center gap-3">
  <Button variant="lightprimary" className="text-sm">
    All User
  </Button>

  <div className="relative flex gap-2 md:gap-5">
    
    {/* ✅ Filter Button (normal) */}
    <Button
      variant="filter"
      className="text-sm"
      onClick={() => setIsFilterOpen(prev => !prev)}
    >
      Filter
    </Button>

    {/* ✅ Filter Panel (conditional) */}
    {isFilterOpen && (
      <div className="absolute top-full right-0 mt-2 w-96 bg-white shadow rounded-lg z-50 p-4">
        <AnaltyicsFilter
          close={() => setIsFilterOpen(false)}
          onApply={(newFilters) => {
            setFilters(newFilters);
            setIsFilterOpen(false);
            setPage(1);
          }}
        />
      </div>
    )}

    {/* Export Button */}
    <Button
      className="bg-primary text-white text-sm"
      onClick={handleExport}
      disabled={isExporting}
    >
      {isExporting ? "Exporting..." : "Export"}
    </Button>
  </div>
</div>

          <div className="overflow-x-auto -mx-3 md:mx-0 px-3 md:px-0">
            <CommonDataTable
              columns={columns}
              data={rows}
              pagination
              paginationServer
              paginationTotalRows={consumerData?.meta_data?.total_records || 0}
              onChangePage={(page) => setPage(page)}
              onChangeRowsPerPage={(newLimit) => {
                setRowsPerPage(newLimit);
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="bg-white rounded-lg shadow p-4">
            <MealAccordion menu={menu} />
          </div>
        </div>

        {isFilterOpen && (
          <AnaltyicsFilter
            close={() => setIsFilterOpen(false)}
            onApply={(newFilters) => {
              setFilters(newFilters);
              setIsFilterOpen(false);
              setPage(1);
            }}
          />
        )}
      </div>
    </>
  );
};

export default Analytics;
